import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/ui/Button";
import ListDisplayGroup from "@/ui/ListDisplayGroup";
import Panel from "@/ui/Panel";
import { ButtonTypes, Variants } from "@/ui/types";
import { ListDropdownItem } from "@/ui/ListDropdown/types";
import { DeletePageModal } from "@/Apps/Funnels/Popups/DeletePageModal";
import { getPagesInCampaign } from "@/Apps/Funnels/widgets/PageList";
import { getImageFromType } from "@/Apps/Funnels/utils";
import { getPageDataFromObjId, openPageInEditor } from "@/api/pages";
import { PasteHtmlModal } from "./PasteHtmlModal";
import { DeleteHtmlPageModal } from "./DeleteHtmlPageModal";
import { PublishHtmlModal } from "./PublishHtmlModal";
import { RenameHtmlPageModal } from "./RenameHtmlPageModal";
import { DropToPublish } from "./DropToPublish";
import { PageThumb } from "./PageThumb";
import { StatusPill, statusOf } from "./StatusPill";

import { LAUNCH_OS_ICON } from "@/brand";
// AMB-695 — "Pages in this site" for Launch Sites. Built from the same widgets as the
// funnel page list (Panel, ListDisplayGroup, DeletePageModal) so it looks the same.
// Round 2 (D15–D17): drop pages and they go live (DropToPublish), live page thumbnails,
// small status pills, and plain "Pages" wording. Drag-and-drop (builder) pages behave as before.

type SitePage = { id: string; slug?: string; type?: string; pageSettings?: any } | undefined;

const isHtml = (page: SitePage) => page?.type === "html";
const isLive = (page: SitePage) => Boolean(page?.pageSettings?.html?.publishedSlug);

export const SitePages = ({ campaignData, onChanged = () => window.location.reload() }: { campaignData: any; onChanged?: () => void }) => {
  const [pasteOpen, setPasteOpen] = useState(false);
  const [deletePageId, setDeletePageId] = useState<string | null>(null);
  const [deleteHtmlPage, setDeleteHtmlPage] = useState<SitePage>(undefined);
  const [renamePage, setRenamePage] = useState<SitePage>(undefined);
  const [publishTarget, setPublishTarget] = useState<{ id: string; name: string; slug: string } | null>(null);
  const navigate = useNavigate();

  const campaignId = campaignData?.id;
  const siteAddress = campaignData?.domain?.name;
  const objects = getPagesInCampaign(campaignData || { id: "", objects: [] }) || [];
  const takenSlugs = objects.map(({ page }) => page?.slug).filter((s): s is string => Boolean(s));
  const pageOf = (objId = ""): SitePage => getPageDataFromObjId(objId, campaignData);

  const liveUrl = (page: SitePage, fallback?: string) =>
    siteAddress ? `https://${siteAddress}/${page?.slug}` : fallback;

  const handlePublish = (objId = "") => {
    const page = pageOf(objId);
    if (!isHtml(page) || !page) return alert("Use Publish Website at the top to publish pages built in the editor.");
    const obj = objects.find((o) => o.id === objId);
    setPublishTarget({ id: page.id, name: obj?.name || "Page", slug: page.slug || "" });
  };

  const menuItems: ListDropdownItem[] = [
    {
      id: "open",
      label: "Open",
      href: "#",
      onClick: (objId = "") => { openItem(objId); },
    },
    {
      id: "rename",
      label: "Rename / change address",
      href: "#",
      onClick: (objId = "") => {
        const page = pageOf(objId);
        if (!isHtml(page)) return alert("Rename pages built in the editor from the Launch Pages editor.");
        const obj = objects.find((o) => o.id === objId);
        setRenamePage({ ...(page as any), name: obj?.name });
      },
    },
    { id: "publish", label: "Publish this page", href: "#", onClick: (objId = "") => { handlePublish(objId); } },
    {
      id: "view",
      label: "View published page",
      href: "#",
      onClick: (objId = "") => {
        const page = pageOf(objId);
        const url = liveUrl(page);
        if (!url) return alert("This site doesn't have a domain yet.");
        window.open(url, "_blank");
      },
    },
    { type: "divider" },
    {
      id: "delete",
      label: "Delete page",
      href: "#",
      onClick: (objId = "") => {
        const page = pageOf(objId);
        if (isHtml(page)) return setDeleteHtmlPage(page);
        setDeletePageId(page?.id || null);
      },
    },
  ];

  const openItem = async (objId: string) => {
    const page = pageOf(objId);
    if (isHtml(page)) return navigate(`/sites/${campaignId}/html/${page?.id}`);
    await openPageInEditor({ campaignId, objectId: objId, pageId: page?.id, navigate, unitName: "sites" });
  };

  const items = objects.map(({ id, name, type, page }) => ({
    id,
    title: name,
    caption: siteAddress ? `${siteAddress}/${page?.slug}` : `/${page?.slug}`,
    image: (
      <PageThumb
        pageId={page?.id}
        isUploaded={isHtml(page)}
        fallback={getImageFromType({ type, subType: page?.type, screenshot: undefined }) || LAUNCH_OS_ICON}
      />
    ),
    actions: isHtml(page) ? <StatusPill status={statusOf(page)} /> : undefined,
    onClick: () => openItem(id),
  }));

  const addButton = (variant?: Variants) => (
    <Button type={ButtonTypes.SOFT} variant={variant} label="+ Add pages" onClick={() => window.dispatchEvent(new Event("sites:browse"))} />
  );

  return (
    <Panel title="Pages" actionButton={addButton()}>
      {pasteOpen && (
        <PasteHtmlModal
          setIsOpen={setPasteOpen}
          campaignId={campaignId}
          siteAddress={siteAddress}
          takenSlugs={takenSlugs}
          onSaved={onChanged}
        />
      )}
      {deletePageId && (
        <DeletePageModal
          campaignId={campaignId}
          pageId={deletePageId}
          setIsOpen={() => setDeletePageId(null)}
          unitName="page"
          onDeleted={onChanged}
        />
      )}
      {renamePage && (
        <RenameHtmlPageModal
          page={renamePage as any}
          campaignId={campaignId}
          siteAddress={siteAddress}
          setIsOpen={(open) => !open && setRenamePage(undefined)}
          onSaved={onChanged}
        />
      )}
      {publishTarget && (
        <PublishHtmlModal
          pages={[publishTarget]}
          siteAddress={siteAddress}
          setIsOpen={(open) => !open && setPublishTarget(null)}
          onPublished={onChanged}
        />
      )}
      {deleteHtmlPage && (
        <DeleteHtmlPageModal
          page={deleteHtmlPage as any}
          siteAddress={siteAddress}
          setIsOpen={(open) => !open && setDeleteHtmlPage(undefined)}
          onDeleted={onChanged}
        />
      )}
      <DropToPublish
        campaignId={campaignId}
        siteAddress={siteAddress}
        pages={objects.filter(({ page }) => page?.id).map(({ name, page }) => ({ id: page!.id, slug: page?.slug, live: isLive(page), name }))}
        onChanged={onChanged}
        onPaste={() => setPasteOpen(true)}
      />
      <ListDisplayGroup
        items={items}
        footer={{ caption: "Built a page with Claude? Drop the file above or paste its code.", actionComponent: addButton(Variants.PRIMARY) }}
        menuItems={menuItems}
      />
    </Panel>
  );
};
